import { useEffect, useMemo, useState } from 'react'
import { isToday } from 'date-fns'

import { useGetUserInfo } from '@/modules/Auth'
import { useNightShiftInfo } from '@/modules/Auth/helpers/useNightShiftInfo'
import { useDateStore } from '@/components/DateInput'
import { useGetHubs } from '../api/useGetHubs'
import { useWorkstationStore } from '../store'
import { HubWorkstation } from '@/types/hub'

export const useWorkstationSelectPM = () => {
    const [selectedValue, setSelectedValue] = useState('')
    const { workstation, setWorkstation, setRoutes, setReassignedRoutes } = useWorkstationStore()
    const { data: hubs, isLoading } = useGetHubs()
    const { data: defaultHubs, isLoading: isLoadingDefault } = useGetHubs(true)
    const { data: userInfo } = useGetUserInfo()
    const { date } = useDateStore()
    const { isNightShift, isAfterMidnight } = useNightShiftInfo()

    const isTodayDate = isToday(date) || (isNightShift && isAfterMidnight())

    const selectedHub = useMemo(
        () => (hubs && userInfo ? hubs?.find((hub) => hub.code === userInfo?.lastSessionDetails.busHubCode) : null),
        [hubs, userInfo]
    )

    const allWorkstations = useMemo(() => (hubs ? hubs.flatMap((item) => item.workstations) : []), [hubs])
    const allDefaultWorkstations = useMemo(
        () => (defaultHubs ? defaultHubs.flatMap((item) => item.workstations) : []),
        [defaultHubs]
    )

    const options = useMemo(() => {
        if (isLoading || !hubs) {
            return []
        }

        return hubs.map((hub) => ({
            label: hub.hubTitle,
            value: hub.code,
            options: hub.workstations.map((workstation) => ({
                label: workstation.workstationNumber,
                value: workstation.workstationNumber,
            })),
        }))
    }, [isLoading, hubs])

    // Get the appropriate workstation data based on whether viewing today or historical
    const getWorkstationData = (workstationNumber: string) => {
        const sourceWorkstations = isTodayDate ? allWorkstations : allDefaultWorkstations
        return sourceWorkstations.find((item) => item.workstationNumber === workstationNumber)
    }

    const selectWorkstation = (workstation: HubWorkstation): void => {
        const { routes, routeReassignments, ...workstationData } = workstation

        setWorkstation(workstationData)
        setRoutes(routes)
        setReassignedRoutes(routeReassignments)
    }

    const handleWorkstationChange = (workstationNumber: string): void => {
        const workstation = getWorkstationData(workstationNumber)

        if (workstation) {
            setSelectedValue(workstationNumber)
            selectWorkstation(workstation)
        }
    }

    const initWorkstation = () => {
        const selectedHub = hubs?.find((hub) => hub.code === userInfo?.lastSessionDetails.busHubCode)

        if (!selectedHub) {
            return
        }

        const defaultWorkstation = selectedHub.workstations.at(0)

        if (defaultWorkstation) {
            handleWorkstationChange(defaultWorkstation.workstationNumber)
        }
    }

    // Initialize workstation when hubs data loads
    useEffect(() => {
        if (!selectedHub) {
            return
        }

        if (!workstation) {
            initWorkstation()
            return
        }

        handleWorkstationChange(workstation.workstationNumber)
    }, [hubs, selectedHub])

    // Update routes in store when date changes (switch between live and default)
    useEffect(() => {
        if (!workstation || isLoadingDefault) {
            return
        }

        const workstationData = getWorkstationData(workstation.workstationNumber)

        if (workstationData) {
            setRoutes(workstationData.routes)
            setReassignedRoutes(workstationData.routeReassignments)
        }
    }, [isTodayDate, allWorkstations, allDefaultWorkstations])

    useEffect(() => {
        if (!workstation) {
            return
        }

        setSelectedValue(workstation.workstationNumber)
    }, [workstation])

    return {
        options,
        selectedValue,
        handleWorkstationChange,
        isLoading: isLoading || isLoadingDefault,
    }
}
